import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { GetGeneralTransferDetailsQuery, GetGeneralTransferDetailsQueryVariables } from 'src/common/api/apollo/types/types';
import { useGeneralSoContext } from 'src/common/context';
import { useGetOfferById } from './useGetOffer.query';

/**
 * Get general (bank transfer) payment details for an offer
 *
 * @param installments - Number of installments (1 for single payment)
 * @returns Bank transfer details: recipient, reference number, amount, customer info
 *
 * @example
 * const { data, loading, error } = useGetGeneralTransferDetails(1);
 */
export const GET_GENERAL_TRANSFER_DETAILS = gql`
  query GetGeneralTransferDetails($offerId: String!, $installments: Int!) {
    getGeneralTransferDetails(offerId: $offerId, installments: $installments) {
      customerAddress
      customerCity
      customerCountry
      customerEmail
      customerFirstName
      customerLastName
      customerOib
      customerPhone
      customerZipCode
      description
      model
      numberOfInstallments
      price
      recipientAccountNumber
      recipientAddress
      recipientCity
      recipientName
      recipientZipCode
      referenceNumber
      referenceNumberType
      referencePrefix
      referenceSuffix
    }
  }
`;

export const useGetGeneralTransferDetails = (installments = 1, offerIdOverride?: string) => {
  const { offerId: offerIdFromContext } = useGeneralSoContext();
  const offerId = offerIdOverride ?? offerIdFromContext;
  const { offer } = useGetOfferById();
  const [error, setError] = useState<Error | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const { data: result, loading, refetch } = useQuery<
    GetGeneralTransferDetailsQuery,
    GetGeneralTransferDetailsQueryVariables
  >(GET_GENERAL_TRANSFER_DETAILS, {
    variables: {
      offerId: offerId!,
      installments,
    },
    skip: !offerId || (!offerIdOverride && !offer),
    onError: (err) => setError(err),
    onCompleted: () => setError(null),
  });

  const isLoading = !isInitialized || loading;

  return {
    data: offerId ? result?.getGeneralTransferDetails : undefined,
    loading: isLoading,
    error,
    refetch,
  };
};
